// Print.com toont bij sommige producten levertijden die het niet levert:
// boekenleggers, deurhangers en tabbladen kennen geen spoed (bevestigd
// 19-10-2026), al staat "Spoed" wel in de catalogus. De prijstabel vroeg die
// kolom bij elke wijziging opnieuw op, en elke geweigerde cel kost bisectie-
// aanvragen bij Print.com. De opgeslagen productgegevens zeggen dit niet, dus
// leren we het uit het antwoord: een levertijdkolom waarvan elke cel als
// leveranciersweigering terugkomt, terwijl een andere kolom wel prijzen gaf,
// vragen we voor diezelfde samenstelling in deze sessie niet meer op.

export type MatrixCellOutcome = {
  columnId: string;
  success: boolean;
  code?: string;
};

/** Codes die zeggen dat Print.com deze regel weigert, niet dat het storing had. */
const REFUSAL_CODES = new Set(['SUPPLIER_CONFIGURATION_UNAVAILABLE', 'SUPPLIER_ITEM_REJECTED']);

/**
 * Kolommen (nooit `standard`) waarin elke cel faalde met een weigering en
 * minstens één cel de expliciete leverancierscode draagt, terwijl een andere
 * kolom wel geprijsd werd. Een storing of rate limit laat dus niets verdwijnen.
 */
export function refusedDeliveryColumnIds(outcomes: MatrixCellOutcome[], protectedIds: string[] = ['standard']): string[] {
  if (!outcomes.some((outcome) => outcome.success)) return [];
  const byColumn = new Map<string, MatrixCellOutcome[]>();
  for (const outcome of outcomes) {
    byColumn.set(outcome.columnId, [...(byColumn.get(outcome.columnId) || []), outcome]);
  }
  const refused: string[] = [];
  for (const [columnId, cells] of byColumn) {
    if (protectedIds.includes(columnId)) continue;
    if (!cells.every((cell) => !cell.success && REFUSAL_CODES.has(String(cell.code)))) continue;
    if (!cells.some((cell) => cell.code === 'SUPPLIER_CONFIGURATION_UNAVAILABLE')) continue;
    refused.push(columnId);
  }
  return refused;
}

/** Samenstelling zonder aantal en levertijd: daarvoor geldt de geleerde weigering. */
export function deliveryRefusalKey(sku: string, options: Record<string, unknown>, ignoredKeys: string[]): string {
  const ignored = new Set(['copies', 'urgency', ...ignoredKeys]);
  const entries = Object.keys(options)
    .filter((key) => !ignored.has(key))
    .sort()
    .map((key) => [key, options[key] === undefined || options[key] === null ? '' : String(options[key])]);
  return JSON.stringify([sku, entries]);
}

const MAX_REMEMBERED = 200;
const refusedByKey = new Map<string, Set<string>>();

export function rememberRefusedDeliveryColumns(key: string, columnIds: string[]) {
  if (!columnIds.length) return;
  const current = refusedByKey.get(key) || new Set<string>();
  columnIds.forEach((id) => current.add(id));
  refusedByKey.delete(key);
  refusedByKey.set(key, current);
  if (refusedByKey.size > MAX_REMEMBERED) refusedByKey.delete(refusedByKey.keys().next().value!);
}

export function refusedDeliveryColumnsFor(key: string): ReadonlySet<string> {
  return refusedByKey.get(key) || new Set<string>();
}

/** Alleen voor tests. */
export function resetRefusedDeliveryColumns() {
  refusedByKey.clear();
}
